/**
 * Invoicing bank accounts (SFE spec §2.32, mandatory mention §3 j).
 *
 * The tenant's list lives in `Tenant.bankReferences` (JSON). Each entry says
 * whether it prints under « RÉFÉRENCES BANCAIRES » and whether it is the
 * default account, printed first. A non-empty list prints at least one
 * account: exactly one entry is the default, and the default is printed.
 *
 * The stored value may still be in the legacy shape
 * `[{ bankName, accountNumber, iban? }]`: its entries read as printed, the
 * first one as the default, with ids derived from their position.
 */
export const BANK_REFERENCE_REQUIRED_MESSAGE = "Les références bancaires sont une mention obligatoire de la facture (§3 j) : au moins un compte bancaire doit rester imprimé sur les factures.";
export const DEFAULT_BANK_ACCOUNT_PRINTED_MESSAGE = "Le compte bancaire par défaut est toujours imprimé sur les factures (mention obligatoire §3 j) : choisissez d'abord un autre compte par défaut.";
export const NO_BANK_REFERENCE_WARNING = "Aucune référence bancaire configurée : mention obligatoire de la facture (§3 j)";
/**
 * The form two account numbers are compared in: control characters and
 * inner runs of spaces collapse to one space, edges trimmed, case kept.
 */
export function bankAccountNumberKey(accountNumber) {
    return accountNumber.replace(/[\u0000-\u001F\u007F]/g, " ").trim().replace(/\s+/g, " ");
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** True when `stored` is an array whose every entry is in the current shape. */
export function isCurrentInvoicingBankAccounts(stored) {
    return (Array.isArray(stored) &&
        stored.every((entry) => isRecord(entry) &&
            typeof entry.id === "string" &&
            entry.id !== "" &&
            typeof entry.bankName === "string" &&
            typeof entry.accountNumber === "string" &&
            (entry.iban === undefined || (typeof entry.iban === "string" && entry.iban !== "")) &&
            typeof entry.printOnInvoices === "boolean" &&
            typeof entry.isDefault === "boolean" &&
            (entry.treasuryAccountId === undefined ||
                (typeof entry.treasuryAccountId === "string" && entry.treasuryAccountId !== ""))));
}
/**
 * The invoicing bank accounts a stored value holds. Entries missing a flag
 * read as printed; when no entry carries `isDefault`, the first one is the
 * default; an entry missing an id gets `compte-<n>`, n being the lowest
 * position-based number no other entry uses. Names, numbers, IBANs and
 * treasury links are kept as stored; an empty or non-string IBAN or link is
 * dropped, entries without a bank name or account number are skipped.
 */
export function normalizeInvoicingBankAccounts(stored) {
    if (!Array.isArray(stored))
        return [];
    const entries = stored.filter((entry) => isRecord(entry) && typeof entry.bankName === "string" && typeof entry.accountNumber === "string");
    const anyDefaultFlag = entries.some((entry) => typeof entry.isDefault === "boolean");
    const usedIds = new Set(entries.map((entry) => entry.id).filter((id) => typeof id === "string" && id !== ""));
    return entries.map((entry, index) => {
        let id = typeof entry.id === "string" && entry.id !== "" ? entry.id : "";
        if (id === "") {
            let n = index + 1;
            while (usedIds.has(`compte-${n}`))
                n++;
            id = `compte-${n}`;
            usedIds.add(id);
        }
        const iban = typeof entry.iban === "string" && entry.iban !== "" ? entry.iban : undefined;
        const treasuryAccountId = typeof entry.treasuryAccountId === "string" && entry.treasuryAccountId !== ""
            ? entry.treasuryAccountId
            : undefined;
        return {
            id,
            bankName: entry.bankName,
            accountNumber: entry.accountNumber,
            ...(iban !== undefined && { iban }),
            printOnInvoices: typeof entry.printOnInvoices === "boolean" ? entry.printOnInvoices : true,
            isDefault: anyDefaultFlag ? entry.isDefault === true : index === 0,
            ...(treasuryAccountId !== undefined && { treasuryAccountId }),
        };
    });
}
/** The first rule `accounts` breaks, or null when the list may be saved. */
export function invoicingBankAccountsIssue(accounts) {
    if (accounts.length === 0)
        return null;
    const invalid = (message) => ({
        code: "REFERENCES_BANCAIRES_INVALIDES",
        message,
    });
    const ids = new Set();
    const numbers = new Set();
    const treasuryAccountIds = new Set();
    for (const account of accounts) {
        if (account.bankName.trim() === "")
            return invalid("Le nom de la banque est obligatoire.");
        const number = bankAccountNumberKey(account.accountNumber);
        if (number === "")
            return invalid("Le numéro de compte est obligatoire.");
        if (ids.has(account.id))
            return invalid("Deux comptes bancaires portent le même identifiant.");
        if (numbers.has(number)) {
            return invalid(`Le compte « ${number} » figure déjà dans la liste des comptes bancaires.`);
        }
        if (account.treasuryAccountId !== undefined) {
            if (treasuryAccountIds.has(account.treasuryAccountId)) {
                return invalid("Deux comptes bancaires de facturation sont liés au même compte de trésorerie.");
            }
            treasuryAccountIds.add(account.treasuryAccountId);
        }
        ids.add(account.id);
        numbers.add(number);
    }
    if (!accounts.some((account) => account.printOnInvoices)) {
        return { code: "REFERENCE_BANCAIRE_OBLIGATOIRE", message: BANK_REFERENCE_REQUIRED_MESSAGE };
    }
    const defaults = accounts.filter((account) => account.isDefault);
    if (defaults.length === 0)
        return invalid("Choisissez le compte bancaire par défaut.");
    if (defaults.length > 1)
        return invalid("Un seul compte bancaire peut être le compte par défaut.");
    if (!defaults[0].printOnInvoices) {
        return { code: "REFERENCE_BANCAIRE_OBLIGATOIRE", message: DEFAULT_BANK_ACCOUNT_PRINTED_MESSAGE };
    }
    return null;
}
/**
 * The references an invoice prints: the printed accounts, the default first,
 * the others in list order. `iban` is present only when the account has one.
 */
export function printedBankReferences(accounts) {
    const printed = accounts.filter((account) => account.printOnInvoices);
    const ordered = [
        ...printed.filter((account) => account.isDefault),
        ...printed.filter((account) => !account.isDefault),
    ];
    return ordered.map((account) => account.iban
        ? { bankName: account.bankName, accountNumber: account.accountNumber, iban: account.iban }
        : { bankName: account.bankName, accountNumber: account.accountNumber });
}
//# sourceMappingURL=invoicing-bank-accounts.js.map